/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles, Button as MaterialButton } from '@material-ui/core';
import Loading from 'components/Loading';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import AddIcon from '@material-ui/icons/Add';
import i18n from 'i18n';
import {
  editApplicationRequest,
  getApplicationsClientsRequest,
  newApplicationRequest,
  removeClientWebApplicationRequest,
  setApplicationRequest,
} from 'modules/clients/actions';
import {
  ClientFields,
  ClientWebApplicationsFields,
  ListClientDataApplicationProps,
} from 'modules/clients/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Permission from 'components/ValidPermission';
import Button from 'components/Button';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { useParams } from 'react-router';
import { useTableFilters } from 'hooks/useTableFilters';
import clsx from 'clsx';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import FormAddWebApplication from './FormAddWebApplication';
import styles from './styles';
import ScreenStatesApplications from './ScreenStatesApplications';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'edit_web_application',
  'delete_web_application',
];

interface Props {
  listApplications: ListClientDataApplicationProps;
  isLoading: boolean;
  client: ClientFields | null;
  permissions: PermissionsData | undefined;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  getApplicationsClientsRequest: ({ id, query, type }) => void;
  removeClientWebApplicationRequest: (props: {
    clientUuid: string;
    appUuid?: string;
    appCodes?: string[];
  }) => void;
  newApplicationRequest: (props: {
    id: string;
    type: string;
    values: any;
    query: typeof initialQuery;
  }) => void;
  editApplicationRequest: (props: {
    id: string;
    type: string;
    app_uuid: string;
    values: any;
    query: typeof initialQuery;
  }) => void;
  setApplicationRequest: (props: {
    id: string;
    type: string;
    app_uuid: string;
    values: any;
    query: typeof initialQuery;
  }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const WebClientApps = ({
  listApplications,
  isLoading,
  client,
  permissions,
  role,
  companySelected,
  getApplicationsClientsRequest,
  removeClientWebApplicationRequest,
  newApplicationRequest,
  editApplicationRequest,
  setApplicationRequest,
  modalClose,
  modalOpen,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
  } = useTableFilters(initialQuery);
  const [refresh, setRefresh] = React.useState(false);
  const { id } = useParams<{ id: string }>();

  const handleFormApplication = () => {
    if (!client) return;

    const { client_uuid } = client;

    modalOpen({
      title: i18n.t('APPLICATIONS_PAGE.ADD_APPLICATION_KEY'),
      maxWidth: 'sm',
      content: (
        <FormAddWebApplication
          onCancel={modalClose}
          onSubmit={values => {
            newApplicationRequest({
              id: client_uuid,
              type: 'web',
              values,
              query,
            });
            modalClose();
          }}
        />
      ),
    });
  };

  const getClientApplications = (refresh = false) => {
    if (client) {
      getApplicationsClientsRequest({
        id: client?.client_uuid,
        type: 'web',
        query,
      });

      setRefresh(refresh);
    }
  };

  React.useEffect(() => {
    getClientApplications();
  }, [id, query]);

  const toggleApplication = (item: ClientWebApplicationsFields) => {
    const actionType = item?.enabled ? 'DISABLE' : 'ENABLE';
    modalOpen({
      title: i18n.t(`APPLICATIONS_PAGE.${actionType}_APPLICATION_KEY`),
      content: i18n.t(
        `APPLICATIONS_PAGE.${actionType}_APPLICATIONS_MINUTES_KEY`
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              setApplicationRequest({
                id: client?.client_uuid || '',
                type: 'web',
                app_uuid: item?.app_uuid,
                values: {
                  enabled: !item?.enabled,
                },
                query,
              });
              modalClose();
            }}
          >
            {i18n.t(`${actionType}_KEY`)}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const getMenuActionItems = (item: ClientWebApplicationsFields) => {
    if (!client) return [];

    return [
      {
        key: 'btn-edit-application',
        label: i18n.t('EDIT_KEY'),
        onClick: () =>
          modalOpen({
            title: i18n.t('APPLICATIONS_PAGE.EDIT_APPLICATION_KEY'),
            maxWidth: 'sm',
            content: (
              <FormAddWebApplication
                initialValues={item}
                onCancel={modalClose}
                onSubmit={({
                  app_uuid,
                  ...values
                }: ClientWebApplicationsFields) => {
                  editApplicationRequest({
                    id: client?.client_uuid,
                    type: 'web',
                    app_uuid,
                    values,
                    query,
                  });

                  modalClose();
                }}
              />
            ),
          }),
        hidden: !permissions?.edit_web_application,
      },
      {
        key: 'btn-disable-application',
        label: i18n.t(item?.enabled ? 'DISABLE_KEY' : 'ENABLE_KEY'),
        onClick: () => toggleApplication(item),
        hidden: !permissions?.edit_web_application,
      },
      {
        key: 'btn-remove-application',
        label: i18n.t('REMOVE_KEY'),
        onClick: () =>
          modalOpen({
            title: i18n.t('APPLICATIONS_PAGE.REMOVE_APPLICATION_KEY'),
            content: i18n.t(
              'APPLICATIONS_PAGE.IRREVERSIBLE_ACTION_APPLICATION_KEY'
            ),
            footer: (
              <Grid>
                <Button
                  submitButton
                  id="submit-button"
                  onClick={() => {
                    removeClientWebApplicationRequest({
                      clientUuid: client?.client_uuid,
                      appUuid: item?.app_uuid,
                    });
                    modalClose();
                  }}
                >
                  {i18n.t('REMOVE_KEY')}
                </Button>
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={modalClose}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            ),
          }),
        hidden: !permissions?.delete_web_application,
      },
    ].filter(action => !action?.hidden);
  };

  return (
    <Grid container>
      {client ? (
        <>
          <Grid container className={classes.content}>
            <Grid
              container
              justify="flex-end"
              alignItems="center"
              className={classes.actions}
            >
              <RefreshButton
                id="btn-refresh"
                onClick={() => getClientApplications(true)}
                animate={isLoading}
                disabled={isLoading}
              />
              <Permission value="create_new_web_application">
                <MaterialButton
                  id="new-app"
                  variant="contained"
                  color="secondary"
                  size="large"
                  className={clsx({
                    [classes.addButtonGreen]: inAdminView(
                      role,
                      companySelected
                    ),
                    [classes.addButtonGray]: inSuperAdminView(
                      role,
                      companySelected
                    ),
                    [classes.addButtonBlue]: inOperatorView(
                      role,
                      companySelected
                    ),
                    [classes.addButtonYellow]: companySelected,
                  })}
                  startIcon={<AddIcon className={classes.addButtonIcon} />}
                  onClick={handleFormApplication}
                >
                  {i18n.t('APPLICATION_KEY')}
                </MaterialButton>
              </Permission>
            </Grid>
            <TableItems
              header={
                <>
                  <TableCell
                    handleOrder={() => handleOrder('name')}
                    order
                    className={classes.rowAllRowsWebApps}
                  >
                    {i18n.t('NAME_KEY')}
                  </TableCell>
                  <TableCell
                    handleOrder={() => handleOrder('url')}
                    order
                    className={classes.rowAllRowsWebApps}
                  >
                    {i18n.t('URL')}
                  </TableCell>
                  <TableCell
                    handleOrder={() => handleOrder('enabled')}
                    order
                    centerTitle
                    className={classes.statusHeader}
                  >
                    {i18n.t('STATUS_KEY')}
                  </TableCell>

                  <Permission value={requiredActionsPermissions}>
                    <TableCell centerTitle>
                      <Grid className={classes.actionHeader}>
                        {i18n.t('ACTIONS_KEY')}
                      </Grid>
                    </TableCell>
                  </Permission>
                </>
              }
              renderItems={(item: ClientWebApplicationsFields, index) => (
                <TableRow key={item?.app_uuid}>
                  <TableCell className={classes.rowAllRowsWebApps}>
                    {item?.name}
                  </TableCell>
                  <TableCell className={classes.rowAllRowsWebApps}>
                    {item?.url}
                  </TableCell>
                  <TableCell centerTitle>
                    <ScreenStatesApplications
                      type={item?.enabled ? 'active' : 'inactive'}
                    />
                  </TableCell>
                  <Permission value={requiredActionsPermissions}>
                    <TableCell align="center">
                      <MenuActions id={`btn-options-apps-${index}`}>
                        {getMenuActionItems(item)?.length > 0 ? (
                          getMenuActionItems(item)?.map(action => (
                            <MenuActionItem
                              key={action?.key}
                              id={action?.key}
                              onClick={action?.onClick}
                            >
                              {action?.label}
                            </MenuActionItem>
                          ))
                        ) : (
                          <MenuActionItem>
                            {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                          </MenuActionItem>
                        )}
                      </MenuActions>
                    </TableCell>
                  </Permission>
                </TableRow>
              )}
              data={listApplications}
              isLoading={!refresh && isLoading}
              onChangePage={handlePageChange}
              onChangeLimit={handleLimitChange}
              notFoundText={i18n.t(
                'APPLICATIONS_PAGE.NO_APPLICATIONS_FOUND_KEY'
              )}
            />
          </Grid>
        </>
      ) : (
        <Loading />
      )}
    </Grid>
  );
};

const mapStateToProps = ({
  clients: { isLoading, listApplications, clientSelected },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  isLoading,
  listApplications,
  companySelected,
  role: data?.role,
  client: clientSelected,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getApplicationsClientsRequest,
  removeClientWebApplicationRequest,
  newApplicationRequest,
  editApplicationRequest,
  setApplicationRequest,
  modalOpen,
  modalClose,
})(WebClientApps);
